import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { applyEventTicketingBackgroundTheme } from '@components/upcoming-event-countdown/upcoming-event-countdown-background.util';
import { DEFAULT_BANNER_CONSTANTS } from '@constants/default-banner.constants';
import { HeaderFooterStateService } from '@modules/shared/services/header-footer-state.service';
import { CommonService } from '@shared/services/common.service';
import { Subject, takeUntil } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ThemedDefaultBannerService implements OnDestroy {
  private themedBackgroundBlobUrl: string | null = null;
  private themedBackgroundRequestId = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly headerFooterStateService: HeaderFooterStateService,
    private readonly cs: CommonService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.headerFooterStateService.themeChangeSubject
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.loadThemedBackgroundImage());
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.revokeThemedBackgroundBlobUrl();
  }

  loadThemedBackgroundImage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const requestId = ++this.themedBackgroundRequestId;

    fetch(DEFAULT_BANNER_CONSTANTS.imagePath)
      .then(response => {
        if (!response.ok) {
          throw new Error(DEFAULT_BANNER_CONSTANTS.errors.loadFailed);
        }

        return response.text();
      })
      .then(svgText => {
        if (requestId !== this.themedBackgroundRequestId) {
          return;
        }

        const themedSvg = applyEventTicketingBackgroundTheme(
          svgText,
          this.document,
          this.cs.isWhiteLabelWeb
        );
        const blob = new Blob([themedSvg], {
          type: DEFAULT_BANNER_CONSTANTS.mimeType
        });
        this.revokeThemedBackgroundBlobUrl();
        this.themedBackgroundBlobUrl = URL.createObjectURL(blob);
        this.document.documentElement.style.setProperty(
          DEFAULT_BANNER_CONSTANTS.cssVariable,
          `url("${this.themedBackgroundBlobUrl}")`
        );
      })
      .catch(() => {
        this.document.documentElement.style.removeProperty(
          DEFAULT_BANNER_CONSTANTS.cssVariable
        );
      });
  }

  private revokeThemedBackgroundBlobUrl(): void {
    if (!this.themedBackgroundBlobUrl) {
      return;
    }

    URL.revokeObjectURL(this.themedBackgroundBlobUrl);
    this.themedBackgroundBlobUrl = null;
  }
}
